import React from "react"

interface PendingFile {
     file: File
     ht: number | ""
     tva: number | ""
}

interface Props {
     files: PendingFile[]
     isSending: boolean
     isExtracting: boolean
     onUpdateData: (index: number, field: "ht" | "tva", value: string) => void
     onRemove: (index: number) => void
     onSubmit: () => void
}

const PendingQueue = ({ files, isSending, isExtracting, onUpdateData, onRemove, onSubmit }: Props) => (
     <div
          className={`mb-12 border border-secondary border-opacity-25 rounded bg-body shadow-sm overflow-hidden animate__animated animate__fadeIn ${
               isExtracting ? "opacity-75" : ""
          }`}
     >
          <div className="bg-body-tertiary px-6 py-4 border-bottom border-secondary border-opacity-25 d-flex justify-content-between align-items-center">
               <h3 className="m-0 fs-4 fw-bold text-gray-900">
                    Fichiers en attente <span className="badge badge-secondary ms-2">{files.length}</span>
               </h3>
               {isExtracting && <span className="spinner-border spinner-border-sm text-primary"></span>}
          </div>

          <div className="p-0">
               {/* --- DESKTOP TABLE VIEW --- */}
               <div className="table-responsive d-none d-md-block">
                    <table className="table table-row-gray-200 align-middle gs-6 gy-4 mb-0">
                         <thead className="bg-light-dark bg-opacity-10 text-uppercase fs-8 fw-bolder text-muted">
                              <tr>
                                   <th>Document</th>
                                   <th className="text-end w-150px">Montant HT (€)</th>
                                   <th className="text-end w-150px">TVA (€)</th>
                                   <th className="w-60px text-end pe-6"></th>
                              </tr>
                         </thead>
                         <tbody>
                              {files.map((item, idx) => {
                                   const isHtInvalid = item.ht === "" || Number(item.ht) <= 0
                                   const isTvaInvalid = item.tva === "" || Number(item.tva) < 0

                                   return (
                                        <tr key={idx}>
                                             <td className="fw-bold text-gray-800 fs-6">
                                                  <div className="d-flex align-items-center">
                                                       <i className="fas fa-file-pdf text-danger fs-4 me-3"></i>
                                                       <span className="text-truncate d-inline-block" style={{ maxWidth: "250px" }} title={item.file.name}>
                                                            {item.file.name}
                                                       </span>
                                                  </div>
                                             </td>
                                             <td className="text-end">
                                                  <div
                                                       className={`input-group input-group-sm border rounded w-125px ms-auto d-flex align-items-center transition-all ${
                                                            isHtInvalid ? "border-danger bg-light-danger bg-opacity-10" : "border-secondary border-opacity-50"
                                                       }`}
                                                  >
                                                       <input
                                                            type="number"
                                                            step="0.01"
                                                            className={`form-control text-end border-0 bg-transparent px-1 fs-7 shadow-none ${
                                                                 !isHtInvalid ? "text-primary fw-bold" : "text-danger"
                                                            }`}
                                                            placeholder="0.00"
                                                            value={item.ht}
                                                            style={{ textAlign: "right" }}
                                                            onChange={e => onUpdateData(idx, "ht", e.target.value)}
                                                       />
                                                       <span className="input-group-text bg-transparent border-0 text-muted ps-0 pe-2 fs-9">
                                                            <i className="fas fa-euro-sign"></i>
                                                       </span>
                                                  </div>
                                             </td>
                                             <td className="text-end">
                                                  <div
                                                       className={`input-group input-group-sm border rounded w-125px ms-auto d-flex align-items-center transition-all ${
                                                            isTvaInvalid ? "border-danger bg-light-danger bg-opacity-10" : "border-secondary border-opacity-50"
                                                       }`}
                                                  >
                                                       <input
                                                            type="number"
                                                            step="0.01"
                                                            className={`form-control text-end border-0 bg-transparent px-1 fs-7 shadow-none ${
                                                                 !isTvaInvalid ? "text-primary fw-bold" : "text-danger"
                                                            }`}
                                                            placeholder="0.00"
                                                            value={item.tva}
                                                            style={{ textAlign: "right" }}
                                                            onChange={e => onUpdateData(idx, "tva", e.target.value)}
                                                       />
                                                       <span className="input-group-text bg-transparent border-0 text-muted ps-0 pe-2 fs-9">
                                                            <i className="fas fa-eur"></i>
                                                       </span>
                                                  </div>
                                             </td>
                                             <td className="text-end pe-6">
                                                  <button
                                                       className="btn btn-icon btn-sm btn-light-danger btn-active-danger rounded-circle w-30px h-30px shadow-none"
                                                       onClick={() => onRemove(idx)}
                                                  >
                                                       <i className="fas fa-times fs-7"></i>
                                                  </button>
                                             </td>
                                        </tr>
                                   )
                              })}
                         </tbody>
                    </table>
               </div>

               {/* --- MOBILE CARD VIEW --- */}
               <div className="d-block d-md-none p-4">
                    {files.map((item, idx) => {
                         const isHtInvalid = item.ht === "" || Number(item.ht) <= 0
                         const isTvaInvalid = item.tva === "" || Number(item.tva) < 0

                         return (
                              <div key={idx} className="card border border-secondary border-opacity-25 mb-4 shadow-none">
                                   <div className="card-body p-4">
                                        <div className="d-flex justify-content-between align-items-start mb-4">
                                             <div className="d-flex align-items-center">
                                                  <i className="fas fa-file-pdf text-danger fs-3 me-3"></i>
                                                  <span className="fw-bold text-gray-800 text-truncate" style={{ maxWidth: "180px" }} title={item.file.name}>
                                                       {item.file.name}
                                                  </span>
                                             </div>
                                             <button className="btn btn-icon btn-sm btn-light-danger rounded-circle" onClick={() => onRemove(idx)}>
                                                  <i className="fas fa-times"></i>
                                             </button>
                                        </div>

                                        <div className="row g-3">
                                             <div className="col-6">
                                                  <label className={`fs-8 text-uppercase fw-bolder mb-1 d-block ${isHtInvalid ? "text-danger" : "text-muted"}`}>
                                                       Montant HT (€)
                                                  </label>
                                                  <div
                                                       className={`input-group border rounded p-1 d-flex align-items-center ${
                                                            isHtInvalid ? "border-danger bg-light-danger bg-opacity-10" : "border-secondary border-opacity-50"
                                                       }`}
                                                  >
                                                       <input
                                                            type="number"
                                                            className="form-control border-0 bg-transparent fs-7 py-1 px-1 shadow-none text-end"
                                                            placeholder="0.00"
                                                            value={item.ht}
                                                            style={{ textAlign: "right" }}
                                                            onChange={e => onUpdateData(idx, "ht", e.target.value)}
                                                       />
                                                       <span className="input-group-text bg-transparent border-0 text-muted ps-0 pe-2 fs-9">
                                                            <i className="fas fa-euro-sign"></i>
                                                       </span>
                                                  </div>
                                             </div>
                                             <div className="col-6">
                                                  <label className={`fs-8 text-uppercase fw-bolder mb-1 d-block ${isTvaInvalid ? "text-danger" : "text-muted"}`}>TVA (€)</label>
                                                  <div
                                                       className={`input-group border rounded p-1 d-flex align-items-center ${
                                                            isTvaInvalid ? "border-danger bg-light-danger bg-opacity-10" : "border-secondary border-opacity-50"
                                                       }`}
                                                  >
                                                       <input
                                                            type="number"
                                                            className="form-control border-0 bg-transparent fs-7 py-1 px-1 shadow-none text-end"
                                                            placeholder="0.00"
                                                            value={item.tva}
                                                            style={{ textAlign: "right" }}
                                                            onChange={e => onUpdateData(idx, "tva", e.target.value)}
                                                       />
                                                       <span className="input-group-text bg-transparent border-0 text-muted ps-0 pe-2 fs-9">
                                                            <i className="fas fa-eur"></i>
                                                       </span>
                                                  </div>
                                             </div>
                                        </div>
                                   </div>
                              </div>
                         )
                    })}
               </div>

               <div className="d-flex justify-content-end p-5 bg-body-tertiary">
                    <button className="btn btn-sm btn-primary fw-bold px-8 py-2 shadow-sm w-100 w-md-auto" onClick={onSubmit} disabled={isSending || isExtracting}>
                         {isSending ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="fas fa-paper-plane me-2 fs-7"></i>}
                         Valider l'envoi
                    </button>
               </div>
          </div>
     </div>
)

export default PendingQueue
